import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './notFound.css';

export default function NotFound() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [glitchActive, setGlitchActive] = useState(false);

  // ── Static / noise canvas ─────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let mounted = true; // stops the loop immediately on unmount

    const draw = () => {
      // Always cancel if unmounted — prevents any further frames
      if (!mounted) return;

      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;

      // Guard: if canvas has no size (hidden / detached) skip this frame
      if (!w || !h || !Number.isFinite(w) || !Number.isFinite(h)) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      canvas.width  = w;
      canvas.height = h;

      // Dark base
      ctx.fillStyle = 'rgba(9,9,11,0.85)';
      ctx.fillRect(0, 0, w, h);

      // Noise grain
      const imageData = ctx.createImageData(w, h);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const v = Math.random() > 0.97 ? Math.floor(Math.random() * 180) : 0;
        data[i]     = v;
        data[i + 1] = v;
        data[i + 2] = v + (Math.random() > 0.995 ? 80 : 0);
        data[i + 3] = v > 0 ? 200 : 0;
      }
      ctx.putImageData(imageData, 0, 0);

      // Scanlines
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      for (let y = 0; y < h; y += 3) {
        ctx.fillRect(0, y, w, 1);
      }

      // Occasional horizontal glitch bars
      if (Math.random() > 0.94) {
        const barY = Math.random() * h;
        const barH = Math.random() * 6 + 1;
        ctx.fillStyle = `rgba(124,58,237,${Math.random() * 0.15})`;
        ctx.fillRect(0, barY, w, barH);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      mounted = false;                        // stops loop on next frame check
      cancelAnimationFrame(rafRef.current);   // cancels the pending frame too
    };
  }, []);

  // ── Periodic glitch burst ─────────────────────────────────────
  useEffect(() => {
    const trigger = () => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 400);
    };

    // First glitch at 800ms, then random intervals
    const first = setTimeout(trigger, 800);
    const interval = setInterval(trigger, 2800 + Math.random() * 2000);

    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="nf-shell">
      {/* Background static canvas */}
      <canvas ref={canvasRef} className="nf-canvas" aria-hidden="true" />

      {/* Film sprocket holes — left */}
      <div className="nf-sprocket nf-sprocket--left" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <div key={i} className="nf-sprocket-hole" />
        ))}
      </div>

      {/* Film sprocket holes — right */}
      <div className="nf-sprocket nf-sprocket--right" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <div key={i} className="nf-sprocket-hole" />
        ))}
      </div>

      {/* Center content */}
      <div className="nf-center">

        {/* Corrupted 404 */}
        <div className={`nf-code-wrap ${glitchActive ? 'glitching' : ''}`} aria-label="404">
          <span className="nf-digit" data-text="4">4</span>
          <span className="nf-digit nf-digit--zero" data-text="0">0</span>
          <span className="nf-digit" data-text="4">4</span>
          {/* Glitch slices */}
          <div className="nf-glitch-slice nf-glitch-slice--a" aria-hidden="true">404</div>
          <div className="nf-glitch-slice nf-glitch-slice--b" aria-hidden="true">404</div>
        </div>

        {/* Film frame indicator */}
        <div className="nf-frame-tag" aria-hidden="true">
          <span className="nf-frame-num">FRAME 000 / NULL</span>
          <span className="nf-frame-tc">00:00:00:00</span>
        </div>

        {/* Message */}
        <div className="nf-message">
          <h1 className="nf-title">SIGNAL LOST</h1>
          <p className="nf-sub">
            This frame doesn't exist in the reel.<br />
            The page you're looking for has been cut.
          </p>
        </div>

        {/* Error code strip */}
        <div className="nf-error-strip" aria-hidden="true">
          <span>ERR_FRAME_NOT_FOUND</span>
          <span>·</span>
          <span>CODEC: NULL</span>
          <span>·</span>
          <span>TIMECODE: --:--</span>
          <span>·</span>
          <span>STATUS: 0x404</span>
        </div>

        {/* Actions */}
        <div className="nf-actions">
          <button
            className="nf-btn nf-btn--primary"
            onClick={() => navigate('/')}
          >
            ▶ Back to Studio
          </button>
          <button
            className="nf-btn nf-btn--ghost"
            onClick={() => navigate(-1)}
          >
            ← Previous Frame
          </button>
        </div>

      </div>

      {/* Bottom ticker */}
      <div className="nf-ticker" aria-hidden="true">
        <div className="nf-ticker-track">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i}>
              PAGE NOT FOUND &nbsp;·&nbsp; 404 ERROR &nbsp;·&nbsp;
              SIGNAL LOST &nbsp;·&nbsp; FRAME MISSING &nbsp;·&nbsp;
              CODEC FAILURE &nbsp;·&nbsp; NO SIGNAL &nbsp;·&nbsp;&nbsp;
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}